// OutOfStockCartNotice
// --------------------
// Tells the customer which items were removed from their cart because
// they are out of stock in the current market. Without this, the purge
// in ShoppingCartContext would make a saved cart silently shrink.
//
// Usage — one line in pages/ShoppingCart.js, directly above the
// `{cart.length === 0 ? ... : ...}` block:
//
//     <OutOfStockCartNotice />
//
// Renders nothing when there is nothing to report.

import React from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useShoppingCart } from '../context/ShoppingCartContext'
import { marketLabel } from '../data/stockOutages'
import { Colors } from '../styles/theme'

export default function OutOfStockCartNotice() {
    const { purgedItems, clearPurgedItems, market } = useShoppingCart()

    if (!purgedItems || purgedItems.length === 0) return null

    const plural = purgedItems.length > 1

    return (
        <Box
            role="status"
            sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                pr: 6,
                position: 'relative',
                border: `1px solid rgba(185, 134, 71, 0.4)`,
                backgroundColor: 'rgba(185, 134, 71, 0.08)',
                borderRadius: 1,
                color: Colors.secondary,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {plural ? 'Some items were' : 'An item was'} removed from your
                cart
            </Typography>
            <Typography variant="body2">
                {purgedItems.join(', ')} — {plural ? 'are' : 'is'} temporarily
                out of stock in {marketLabel(market)}. Contact{' '}
                <a href="mailto:info@electrobraid.com">info@electrobraid.com</a>{' '}
                for an ETA or to arrange a substitute. We are very sorry for the
                inconvenience.
            </Typography>

            <IconButton
                onClick={clearPurgedItems}
                aria-label="Dismiss"
                sx={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 40,
                    height: 40,
                    color: Colors.secondary,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 82, 71, 0.08)',
                        color: Colors.greenLight,
                    },
                }}
            >
                <CloseIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
        </Box>
    )
}
